import React, { useState, useEffect, useMemo, useRef } from "react";
import { useTenant } from "../../contexts/TenantContext";
import { authService } from "../../services/authService";
import { analyticsService } from "../../services/analyticsService";
import { useAuthActions } from "@/hooks/auth/useAuth";
import { useURLParams } from "../../contexts/URLParamsContext";
import useContactAutoFill from "../../hooks/contacts/useContactAutoFill";
import useEmailRegistrationCheck from "../../hooks/auth/useEmailRegistrationCheck";
import { X, ArrowRight } from "lucide-react";
import { getAuth } from "firebase/auth";
import { firebaseService } from "../../lib/firebase";
import GoogleSignInButton from "./GoogleSignInButton";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  squeezeRegisterSchema,
  SqueezeRegisterFormData,
} from "../../schemas/auth";
import { replaceDynamicTags, withPolicyLinks } from "../../utils/dynamicTagsUtils";
import { resolveIdxConsentStyle } from "../../config/consentStyle";
import SplitConsentCheckboxes from "./SplitConsentCheckboxes";
import { useIdxConsentProfile } from "../../hooks/auth/useIdxConsentProfile";
import {
  captureDisplayedSplitConsent,
  type IdxConsentReceipt,
  type SplitConsentSnapshot,
} from "../../config/idxConsentReceipts";

interface SqueezePageOverlayProps {
  onClose: () => void;
  onSuccess: (isExistingUser: boolean) => void;
  propertyId?: string;
  propertyDetails?: {
    address?: string;
    city?: string;
    state?: string;
    zipCode?: string;
    propertyType?: string;
    beds?: number;
    baths?: number;
    sqft?: number;
    listPrice?: number;
  };
  isConfirmationMode?: boolean;
  closeOnlyModal?: boolean;
  hideGoogleSignIn?: boolean;
  hasMissingContact?: boolean;
}

export const SqueezePageOverlay: React.FC<SqueezePageOverlayProps> = ({
  onClose,
  onSuccess,
  propertyId,
  propertyDetails,
  isConfirmationMode = false,
  closeOnlyModal = false,
  hideGoogleSignIn = false,
  hasMissingContact = false,
}) => {
  const { config } = useTenant();
  const { setIsInGoogleSignInFlow } = useAuthActions();
  const { params: urlParams } = useURLParams();
  const { profile: idxConsentProfile, loading: idxConsentProfileLoading } =
    useIdxConsentProfile(config?.id);
  if (!config) return null;

  const [isLogin, setIsLogin] = useState(false);
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [hasManuallyToggled, setHasManuallyToggled] = useState(false);
  const [consent, setConsent] = useState(false);
  const [textConsent, setTextConsent] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);
  const consentSnapshotRef = useRef<SplitConsentSnapshot | null>(null);

  // The server's FUB capture scope selects the existing split groups. Other
  // orgs retain their configured style or the SPEC-2232 fallback.
  const consentStyle = resolveIdxConsentStyle(
    config.idxConsentStyle,
    idxConsentProfile?.capture_enabled === true
  );

  // One bundled checkbox covering all three channels, which blocks submission.
  const showConsentCheckbox = consentStyle === "bundled";

  // Two optional checkboxes splitting the text opt-in from calls-and-email.
  const showSplitConsent = consentStyle === "split";

  // Whenever a box is on screen its value is the consent of record; only the
  // passive treatment, which renders no box at all, implies consent.
  const consentIsExplicit = consentStyle !== "passive";

  // The bundled checkbox is the only one that gates the form: the split boxes
  // stay optional and passive orgs have nothing to tick.
  const consentRequired = showConsentCheckbox;
  const consentMissing = consentRequired && !consent;

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
    trigger,
  } = useForm<SqueezeRegisterFormData>({
    resolver: zodResolver(squeezeRegisterSchema),
    mode: "onChange",
  });

  // Use the contact auto-fill hook to pre-fill the form when contact_id is present
  const contactAutoFill = useContactAutoFill({
    contactId: urlParams.contact_id,
    setValue,
    trigger,
  });

  // Check if email from contact is registered (only when contact_id is in URL)
  const emailToCheck = watch("email");
  const { isRegistered, isChecking } = useEmailRegistrationCheck({
    email: emailToCheck,
    organizationId: config?.id,
    enabled: !!urlParams.contact_id && !!emailToCheck, // Only check when contact_id exists
  });

  // Auto-switch to login mode if email is registered (but only if user hasn't manually toggled)
  useEffect(() => {
    if (isRegistered === true && !isLogin && !hasManuallyToggled) {
      setIsLogin(true);
    }
  }, [isRegistered, isLogin, hasManuallyToggled, emailToCheck]);

  // Merge loading and error states
  const formLoading =
    isLoading || contactAutoFill.isLoading || isAuthenticating || isChecking ||
    (!isLogin && idxConsentProfileLoading);
  const formError = error || contactAutoFill.error;

  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      const email = watch("email");

      const userCredential = await firebaseService.auth.signIn({
        email,
        password,
      });


      analyticsService.trackEvent({
        orgId: config?.id || "default",
        tenantId: config.id,
        eventType: "conversion",
        category: "Registration",
        action: "Login Success",
        isAuthenticated: true,
        userType: "lead",
        data: {
          userId: userCredential.user.uid,
          path: window.location.pathname,
          ...(propertyId ? { propertyId } : {}),
          ...(propertyDetails || {}),
          tenantName: config.name,
          source: document.referrer,
          utmSource:
            new URLSearchParams(window.location.search).get("utm_source") ||
            "direct",
        },
      });

      // Keep button disabled while waiting for auth state to update
      setIsAuthenticating(true);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Invalid email or password"
      );
    } finally {
      setIsLoading(false);
    }
  };

  const onSubmit = async (data: SqueezeRegisterFormData) => {
    if (idxConsentProfileLoading) return;

    // Guards the function rather than just the submit button, so the Google
    // sign-in path — which calls handleSubmit directly — is gated too.
    if (consentMissing) {
      setError("Please agree to be contacted before continuing.");
      setIsInGoogleSignInFlow(false);
      return;
    }

    setError(null);
    setIsLoading(true);

    // Ensure the Google sign-in flow flag is cleared
    setIsInGoogleSignInFlow(false);

    const agent = urlParams.agent;

    analyticsService.trackEvent({
      orgId: config?.id || "default",
      tenantId: config.id,
      eventType: "interaction",
      category: "Squeeze Page",
      action: "Form Start",
      isAuthenticated: false,
      userType: "visitor",
      data: {
        path: window.location.pathname,
        ...(propertyId ? { propertyId } : {}),
        tenantName: config.name,
      },
    });

    try {
      let gaClientId = "";
      if (window.gtag && config.analytics?.gaTrackingId) {
        try {
          // Add a timeout to prevent the Promise from hanging indefinitely
          gaClientId = await Promise.race([
            // The gtag Promise
            new Promise<string>((resolve) => {
              try {
                window.gtag?.(
                  "get",
                  config.analytics.gaTrackingId,
                  "client_id",
                  (clientId: string) => {
                    resolve(clientId || "");
                  }
                );
              } catch {
                console.warn("GA client ID lookup failed");
                resolve("");
              }
            }),
            // Timeout Promise - resolves after 1 second
            new Promise<string>((resolve) => {
              setTimeout(() => {
                console.warn("GA client ID retrieval timed out");
                resolve("");
              }, 1000);
            }),
          ]);
        } catch {
          console.warn("GA client ID lookup failed");
          // Continue with empty gaClientId
        }
      }

      let consentReceipts: IdxConsentReceipt[] | undefined;
      if (idxConsentProfile?.capture_enabled === true) {
        const snapshot = await captureDisplayedSplitConsent(
          formRef.current,
          {
            email: data.email,
            phoneNumber: data.phoneNumber,
            callsAndEmailConsent: consent,
            textConsent,
            surface: "idx-property-squeeze",
            sourceUrl: window.location.href,
          },
          consentSnapshotRef.current
        );
        consentSnapshotRef.current = snapshot;
        consentReceipts = snapshot.receipts;
      }
      const userCredential = await authService.register({
        email: data.email,
        password: data.phoneNumber,
        firstName: data.firstName,
        lastName: data.lastName,
        phoneNumber: data.phoneNumber,
        marketingConsent: consentIsExplicit ? consent : true,
        consentReceipts,
        gaClientId,
        agent,
        tenantId: config.id, // Pass the tenant ID from the config
      });

      analyticsService.trackEvent({
        orgId: config?.id || "default",
        tenantId: config.id,
        eventType: "conversion",
        category: "Registration",
        action: "Complete",
        isAuthenticated: true,
        userType: "lead",
        data: {
          userId: userCredential.user.uid,
          path: window.location.pathname,
          ...(propertyId ? { propertyId } : {}),
          ...(propertyDetails || {}),
          tenantName: config.name,
          source: document.referrer,
          utmSource:
            new URLSearchParams(window.location.search).get("utm_source") ||
            "direct",
        },
      });

      // Explicit dataLayer push so GTM Custom Event triggers fire reliably,
      // independent of which gtag implementation won the init race.
      if (typeof window !== "undefined" && Array.isArray((window as any).dataLayer)) {
        (window as any).dataLayer.push({
          event: "Complete",
          event_category: "Registration",
          propertyId,
          tenantId: config.id,
        });
      }

      // Keep button disabled while waiting for auth state to update
      setIsAuthenticating(true);
    } catch (err) {
      // Ensure flag is cleared on error too
      setIsInGoogleSignInFlow(false);
      analyticsService.trackEvent({
        orgId: config?.id || "default",
        tenantId: config.id,
        eventType: "error",
        category: "Registration",
        action: "Error",
        isAuthenticated: false,
        userType: "visitor",
        data: {
          errorCode: "REGISTRATION_FAILED",
          path: window.location.pathname,
          ...(propertyId ? { propertyId } : {}),
          ...(propertyDetails || {}),
          tenantName: config.name,
        },
      });

      setError(
        err instanceof Error
          ? err.message
          : "An error occurred during registration"
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 z-50 squeeze-page-overlay"
      style={{ backdropFilter: "none" }}
    >
      <div
        className="min-h-[100dvh] py-3 px-3 flex items-center justify-center"
        style={{ backdropFilter: "none" }}
      >
        <div
          className="bg-white rounded-lg shadow-xl max-w-md w-full px-4 md:px-6 py-6 md:py-8 max-h-[calc(100dvh-1.5rem)] overflow-y-auto custom-scrollbar"
          style={{ filter: "none" }}
        >
          <div className="relative mb-3 md:mb-4">
            {/* Only show close button if closeOnlyModal is true (property is being saved) */}
            {!isConfirmationMode && closeOnlyModal && (
              <button
                onClick={(e) => {
                  e.preventDefault();
                  onClose();
                }}
                className="p-1 absolute right-0 top-0 mr-[-10px] text-gray-400 hover:text-gray-600 !bg-transparent flex items-center justify-center"
                aria-label="Close"
              >
                <X size={18} />
              </button>
            )}
            <div className="text-center px-2">
              <h2 className="text-lg lg:text-[22px] font-bold text-gray-900 leading-[1.15em] lg:leading-[1.3em]">
                {isLogin
                  ? "Welcome Back"
                  : isConfirmationMode
                  ? "Confirm your details"
                  : "Register For Free"}
              </h2>

              {hasMissingContact ? (
                <>
                  <div className="mt-2 bg-red-50 p-2 rounded-md">
                    <p className="text-xs lg:text-sm text-red-800">
                      There appears to be an issue with your account. Please
                      contact your website administrator or create a new account
                      to continue accessing all features.
                    </p>
                  </div>
                </>
              ) : (
                <p className="mt-1 text-xs lg:text-sm text-gray-600">
                  {isLogin
                    ? "Sign in to access exclusive listings and full property details!"
                    : "Sign Up for FREE access to exclusive listings and full property details! (Prices and inventory updated 24/7)"}
                </p>
              )}
            </div>
          </div>

          {!hideGoogleSignIn && (
            <>
              <GoogleSignInButton
                onExistingUser={onSuccess}
                onNewUser={(data: {
                  firstName: string;
                  lastName: string;
                  email: string;
                }) => {
                  setValue("firstName", data.firstName);
                  setValue("lastName", data.lastName);
                  setValue("email", data.email);
                  handleSubmit(onSubmit)();
                }}
                isLoading={isLoading}
                setIsLoading={setIsLoading}
                setError={setError}
                config={config}
                propertyId={propertyId}
              />

              <div className="my-3 md:my-4" />
            </>
          )}

          <form
            ref={formRef}
            noValidate
            onChange={() => {
              consentSnapshotRef.current = null;
            }}
            onSubmit={isLogin ? handleEmailLogin : handleSubmit(onSubmit)}
            className="space-y-3"
          >
            {!isLogin && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <input
                    id="firstName"
                    type="text"
                    placeholder="First Name"
                    {...register("firstName")}
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-[var(--primary)] focus:ring-[var(--primary)] text-base py-3 px-3"
                    disabled={formLoading}
                  />
                  {errors.firstName && (
                    <p className="mt-1 text-xs text-red-600">
                      {errors.firstName.message}
                    </p>
                  )}
                </div>
                <div>
                  <input
                    id="lastName"
                    type="text"
                    placeholder="Last Name"
                    {...register("lastName")}
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-[var(--primary)] focus:ring-[var(--primary)] text-base py-3 px-3"
                    disabled={formLoading}
                  />
                  {errors.lastName && (
                    <p className="mt-1 text-xs text-red-600">
                      {errors.lastName.message}
                    </p>
                  )}
                </div>
              </div>
            )}

            <div>
              <input
                id="email"
                type="email"
                placeholder="Email"
                {...register("email")}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-[var(--primary)] focus:ring-[var(--primary)] text-base py-3 px-3"
                disabled={formLoading}
              />
              {errors.email && (
                <p className="mt-1 text-xs text-red-600">
                  {errors.email.message}
                </p>
              )}
            </div>

            {!isLogin && (
              <div>
                <input
                  id="phoneNumber"
                  type="tel"
                  placeholder="Phone Number"
                  {...register("phoneNumber")}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-[var(--primary)] focus:ring-[var(--primary)] text-base py-3 px-3"
                  disabled={formLoading}
                />
                {errors.phoneNumber && (
                  <p className="mt-1 text-xs text-red-600">
                    {errors.phoneNumber.message}
                  </p>
                )}
                <p className="mt-1 text-[0.65rem] text-gray-500">
                  Your phone number will be used as your password
                </p>
              </div>
            )}

            {isLogin && (
              <div>
                <input
                  id="password"
                  type="password"
                  placeholder="Password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-[var(--primary)] focus:ring-[var(--primary)] text-base py-3 px-3"
                  disabled={formLoading}
                />
              </div>
            )}

            {formError && (
              <p className="text-sm font-medium text-red-600">{formError}</p>
            )}

            <div>
              <button
                type="submit"
                disabled={formLoading}
                style={{ color: config.branding?.colors?.primary }}
                className="w-full flex items-center justify-center gap-2 py-1 px-2 text-xl lg:text-2xl font-semibold underline underline-offset-4 hover:opacity-80 !bg-transparent disabled:opacity-50 group"
              >
                <span>
                  {idxConsentProfileLoading && !isLogin
                    ? "Loading consent options..."
                    : formLoading
                    ? "Processing..."
                    : isLogin
                    ? "Sign In"
                    : isConfirmationMode
                    ? "Confirm"
                    : "Continue to Photos"}
                </span>
                <ArrowRight
                  size={24}
                  className="transition-transform group-hover:translate-x-1"
                />
              </button>
            </div>

            {!isLogin && showSplitConsent && (
              <SplitConsentCheckboxes
                orgName={config.name}
                callsAndEmailConsent={consent}
                onCallsAndEmailConsentChange={setConsent}
                textConsent={textConsent}
                onTextConsentChange={setTextConsent}
                disabled={formLoading}
                idPrefix="squeeze"
                className="text-[0.55rem] lg:text-[0.6rem] text-gray-500 leading-tight"
              />
            )}

            {!isLogin && !showSplitConsent && (
              <div className="text-[0.55rem] lg:text-[0.6rem] text-gray-500 leading-tight">
                {showConsentCheckbox && (
                  <input
                    id="squeeze-consent"
                    type="checkbox"
                    checked={consent}
                    onChange={(e) => setConsent(e.target.checked)}
                    disabled={formLoading}
                    className="mr-1.5 inline-block h-4 w-4 align-[-3px] rounded border-gray-300 text-[var(--primary)] focus:ring-[var(--primary)]"
                  />
                )}
                <span
                  className={showConsentCheckbox ? "cursor-pointer" : undefined}
                  onClick={(e) => {
                    // Toggle consent when the text is clicked (only when the
                    // checkbox is shown); let embedded policy links behave normally.
                    if (!showConsentCheckbox) return;
                    if ((e.target as HTMLElement).closest("a")) return;
                    if (!formLoading) setConsent(!consent);
                  }}
                >

                {config.branding?.squeezePage?.language?.trim() ? (
                  <span
                    dangerouslySetInnerHTML={{
                      __html: replaceDynamicTags(
                        withPolicyLinks(config.branding.squeezePage.language)
                      ),
                    }}
                  />
                ) : (
                  <>
                    {showConsentCheckbox
                      ? "Yes, I agree to be contacted by "
                      : "By clicking Continue to Photos, you agree to be contacted by "}
                    {config.name} via call, email, and text for real estate
                    services, including robocalls, automated calls and texts,
                    and AI-powered communications. You understand that consent
                    is not required to purchase and that you can opt out at any
                    time. To opt out, you can reply 'stop' at any time. You can
                    also click the unsubscribe link in the emails. Data will not
                    be sold or shared with third parties for promotional or
                    marketing purposes. Message and data rates may apply.
                    Message frequency may vary.{" "}
                    <a
                      href="/terms"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gray-600 hover:text-blue-800 underline"
                    >
                      Terms of Service
                    </a>{" "}
                    and{" "}
                    <a
                      href="/privacy"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gray-600 hover:text-blue-800 underline"
                    >
                      Privacy Policy
                    </a>
                    .
                  </>
                )}
                </span>
              </div>
            )}
          </form>

          <div className="mt-2 text-center">
            <button
              onClick={() => {
                // Mark that user has manually toggled
                setHasManuallyToggled(true);

                // Clear error state when switching modes
                setError(null);

                // Toggle login mode
                setIsLogin(!isLogin);

                // If switching to login mode, clear registration data
                if (!isLogin) {
                  // Clear registration data from localStorage
                  authService.clearRegistrationData();
                }
              }}
              className="text-xs underline hover:text-blue-600 !bg-transparent"
            >
              {isLogin
                ? "Don't have an account? Sign up"
                : "Already have an account? Sign in"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SqueezePageOverlay;
